import { createAuthRequestInit } from '../utils/auth';
import { API_BASE } from '../config/api';

export type ActivityType =
  | 'login'
  | 'template_change'
  | 'campaign_change'
  | 'settings_change'
  | 'group_change';

export type ActivityCategory = 'login' | 'system';

export interface ActivityLog {
  id: string;
  userId: string | null;
  userName: string;
  userEmail?: string | null;
  userRole?: string | null;
  activityType: ActivityType;
  description: string;
  createdAt: string;
}

export interface GetActivityLogsParams {
  category?: ActivityCategory;
  userScope?: 'all' | 'active' | 'inactive';
  range?: '24h' | '7d' | '30d';
  module?: 'all' | 'templates' | 'campaigns' | 'settings' | 'groups';
  limit?: number;
  types?: ActivityType[];
  startDate?: string;
  endDate?: string;
}

function mapApiActivityLog(item: any): ActivityLog {
  const type =
    (item?.activityType || item?.activity_type || 'login').toLowerCase() as ActivityType;
  const userName =
    item?.userName ||
    item?.user_name ||
    [item?.firstName || item?.first_name, item?.lastName || item?.last_name]
      .filter(Boolean)
      .join(' ') ||
    'System';

  return {
    id: String(item?.id ?? ''),
    userId: item?.userId ?? item?.user_id ?? null,
    userName,
    userEmail: item?.userEmail ?? item?.user_email ?? null,
    userRole: item?.userRole ?? item?.role ?? null,
    activityType: type,
    description: item?.description || '',
    createdAt: item?.createdAt || item?.created_at || new Date().toISOString()
  };
}

function generateFallbackActivityLogs(): ActivityLog[] {
  const now = new Date();
  const minutesAgo = (mins: number) => {
    const d = new Date(now);
    d.setMinutes(d.getMinutes() - mins);
    return d.toISOString();
  };

  return [
    {
      id: 'mock-login-1',
      userId: 'user-1',
      userName: 'Ariana Daniels',
      userEmail: 'ariana@example.com',
      userRole: 'super_admin',
      activityType: 'login',
      description: 'Ariana logged in from Chrome on Windows',
      createdAt: minutesAgo(12)
    },
    {
      id: 'mock-login-2',
      userId: 'user-2',
      userName: 'Craig Sullivan',
      userEmail: 'craig@example.com',
      userRole: 'admin',
      activityType: 'login',
      description: 'Craig authenticated via SSO',
      createdAt: minutesAgo(47)
    },
    {
      id: 'mock-system-1',
      userId: 'user-1',
      userName: 'Ariana Daniels',
      userEmail: 'ariana@example.com',
      userRole: 'super_admin',
      activityType: 'settings_change',
      description: 'Updated outbound SMS provider credentials',
      createdAt: minutesAgo(75)
    },
    {
      id: 'mock-system-2',
      userId: 'user-3',
      userName: 'Kimberly James',
      userEmail: 'kimberly@example.com',
      userRole: 'admin',
      activityType: 'template_change',
      description: 'Modified “Promo Blast” template content',
      createdAt: minutesAgo(95)
    },
    {
      id: 'mock-system-3',
      userId: 'user-4',
      userName: 'Lucas Mejia',
      userEmail: 'lucas@example.com',
      userRole: 'admin',
      activityType: 'group_change',
      description: 'Added 125 contacts to “VIP Retailers” group',
      createdAt: minutesAgo(140)
    }
  ];
}

function applyFallbackFilters(logs: ActivityLog[], params: GetActivityLogsParams): ActivityLog[] {
  let filtered = logs;

  if (params.category === 'login') {
    filtered = filtered.filter((log) => log.activityType === 'login');
  } else if (params.category === 'system') {
    filtered = filtered.filter((log) => log.activityType !== 'login');
  }

  if (params.module && params.module !== 'all') {
    const moduleMap: Record<'templates' | 'campaigns' | 'settings' | 'groups', ActivityType[]> = {
      templates: ['template_change'],
      campaigns: ['campaign_change'],
      settings: ['settings_change'],
      groups: ['group_change']
    };
    const allowed = moduleMap[params.module as keyof typeof moduleMap];
    if (allowed) {
      filtered = filtered.filter((log) => allowed.includes(log.activityType));
    }
  }

  if (params.types?.length) {
    const typeSet = new Set(params.types);
    filtered = filtered.filter((log) => typeSet.has(log.activityType));
  }

  const limit = params.limit ?? 8;
  return filtered.slice(0, limit);
}

export async function getActivityLogs(params: GetActivityLogsParams = {}): Promise<ActivityLog[]> {
  const query = new URLSearchParams();
  if (params.category) query.append('category', params.category);
  if (params.userScope && params.userScope !== 'all') query.append('userScope', params.userScope);
  if (params.range) query.append('range', params.range);
  if (params.module) query.append('module', params.module);
  if (params.limit) query.append('limit', String(params.limit));
  if (params.types?.length) query.append('types', params.types.join(','));
  if (params.startDate) query.append('startDate', params.startDate);
  if (params.endDate) query.append('endDate', params.endDate);

  try {
    const res = await fetch(
      `${API_BASE}/activity/logs${query.toString() ? `?${query.toString()}` : ''}`,
      createAuthRequestInit()
    );

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(txt || 'Failed to load activity logs');
    }

    const json = await res.json();
    const rawLogs = Array.isArray(json?.data?.logs) ? json.data.logs : [];
    return rawLogs.map(mapApiActivityLog);
  } catch (error) {
    console.warn('Falling back to mock activity logs:', error);
    return applyFallbackFilters(generateFallbackActivityLogs(), params);
  }
}


